import { Plus, Trash2 } from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";
import {
  Alert,
  Button,
  Checkbox,
  Input,
  Modal,
  Select,
  Spinner,
  Textarea,
} from "@/components/common";
import { useContestOptions, useSaveContest } from "@/hooks/useContests";
import type {
  Contest,
  ContestMeasure,
  ContestOption,
  ContestRewardType,
  ContestTier,
} from "@/types/contests";
import {
  isMoney,
  MEASURE_HINTS,
  MEASURE_LABELS,
  REWARD_TYPE_LABELS,
} from "./format";

export interface ContestTarget {
  /** null creates a new contest (saved as a draft). */
  contest: Contest | null;
}

function errorMessage(error: unknown, fallback: string) {
  return (error as { message?: string } | null)?.message ?? fallback;
}

function toNumber(text: string): number | null {
  const value = Number(text.replace(",", ".").replace(/\s/g, ""));
  return text.trim() === "" || Number.isNaN(value) ? null : value;
}

function monthBounds() {
  const now = new Date();
  const pad = (n: number) => String(n).padStart(2, "0");
  const last = new Date(now.getFullYear(), now.getMonth() + 1, 0).getDate();
  const prefix = `${now.getFullYear()}-${pad(now.getMonth() + 1)}`;
  return { start: `${prefix}-01`, end: `${prefix}-${pad(last)}` };
}

const MEASURE_OPTIONS = (Object.keys(MEASURE_LABELS) as ContestMeasure[]).map(
  (value) => ({ value, label: MEASURE_LABELS[value] }),
);

const REWARD_OPTIONS = (
  Object.keys(REWARD_TYPE_LABELS) as ContestRewardType[]
).map((value) => ({ value, label: REWARD_TYPE_LABELS[value] }));

/** "All" or a picked subset — an empty selection means all. */
function PickList({
  label,
  allLabel,
  options,
  selected,
  onChange,
  disabled,
}: {
  label: string;
  allLabel: string;
  options: ContestOption[];
  selected: string[];
  onChange: (ids: string[]) => void;
  disabled?: boolean;
}) {
  const [picking, setPicking] = useState(selected.length > 0);
  const all = !picking || disabled;

  return (
    <fieldset className="space-y-2" disabled={disabled}>
      <legend className="text-sm font-medium text-sub">{label}</legend>
      <div className="flex flex-wrap gap-4">
        <Checkbox
          label={allLabel}
          checked={all}
          onChange={(e) => {
            setPicking(!e.target.checked);
            if (e.target.checked) {
              onChange([]);
            }
          }}
        />
      </div>
      {!all && (
        <div className="max-h-40 space-y-1.5 overflow-y-auto rounded-lg border border-edge-soft p-3">
          {options.length === 0 ? (
            <p className="text-xs text-faint">Brak pozycji do wyboru.</p>
          ) : (
            options.map((o) => (
              <Checkbox
                key={o.id}
                label={o.name}
                checked={selected.includes(o.id)}
                onChange={(e) => {
                  onChange(
                    e.target.checked
                      ? [...selected, o.id]
                      : selected.filter((id) => id !== o.id),
                  );
                }}
              />
            ))
          )}
        </div>
      )}
    </fieldset>
  );
}

interface TierRow {
  threshold: string;
  reward: string;
}

export function ContestModal({
  target,
  onClose,
}: {
  target: ContestTarget | null;
  onClose: () => void;
}) {
  const save = useSaveContest();
  const options = useContestOptions(target !== null);

  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [measure, setMeasure] = useState<ContestMeasure>("Premium");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [insurerIds, setInsurerIds] = useState<string[]>([]);
  const [productNames, setProductNames] = useState<string[]>([]);
  const [agentIds, setAgentIds] = useState<string[]>([]);
  const [minimum, setMinimum] = useState("");
  const [rewardType, setRewardType] = useState<ContestRewardType>("TopPlaces");
  const [topPlaces, setTopPlaces] = useState("3");
  const [tiers, setTiers] = useState<TierRow[]>([]);
  const [rewardDescription, setRewardDescription] = useState("");

  // The modal stays mounted: fill the form each time it opens for another contest.
  const [openedFor, setOpenedFor] = useState<ContestTarget | null>(null);
  if (target !== openedFor) {
    setOpenedFor(target);
    const c = target?.contest;
    const month = monthBounds();
    setName(c?.name ?? "");
    setDescription(c?.description ?? "");
    setMeasure(c?.measure ?? "Premium");
    setStartDate(c?.startDate ?? month.start);
    setEndDate(c?.endDate ?? month.end);
    setInsurerIds(c?.insurerIds ?? []);
    setProductNames(c?.productNames ?? []);
    setAgentIds(c?.participantAgentIds ?? []);
    setMinimum(c?.minimumThreshold?.toString() ?? "");
    setRewardType(c?.rewardType ?? "TopPlaces");
    setTopPlaces(c?.topPlaces?.toString() ?? "3");
    setTiers(
      c?.tiers.map((t) => ({
        threshold: t.threshold.toString(),
        reward: t.reward,
      })) ?? [
        { threshold: "", reward: "" },
        { threshold: "", reward: "" },
      ],
    );
    setRewardDescription(c?.rewardDescription ?? "");
  }

  const isNew = target?.contest === null;
  const newClients = measure === "NewClients";
  const unit = isMoney(measure)
    ? "zł"
    : measure === "NewClients"
      ? "klientów"
      : "polis";
  const parsedTiers: ContestTier[] = tiers.map((t) => ({
    threshold: toNumber(t.threshold) ?? 0,
    reward: t.reward.trim(),
  }));
  const minimumValue = toNumber(minimum);

  const problem = (() => {
    if (!name.trim()) {
      return "Podaj nazwę konkursu.";
    }
    if (!startDate || !endDate || endDate < startDate) {
      return "Podaj poprawny okres (data zakończenia nie wcześniej niż rozpoczęcia).";
    }
    if (rewardType === "TopPlaces" && !(Number(topPlaces) >= 1)) {
      return "Podaj liczbę nagradzanych miejsc.";
    }
    if (rewardType === "AboveThreshold" && minimumValue === null) {
      return "Przy nagradzaniu wszystkich powyżej progu podaj próg minimalny.";
    }
    if (rewardType === "Tiers") {
      if (parsedTiers.some((t) => t.threshold <= 0 || !t.reward)) {
        return "Każdy próg musi mieć wartość i nagrodę.";
      }
      if (
        parsedTiers.some(
          (t, i) =>
            i > 0 && t.threshold <= (parsedTiers[i - 1]?.threshold ?? 0),
        )
      ) {
        return "Progi muszą rosnąć (np. 20, 40, 60).";
      }
    }
    if (!rewardDescription.trim()) {
      return "Opisz nagrodę.";
    }
    return null;
  })();

  function handleClose() {
    save.reset();
    onClose();
  }

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!target || problem) {
      return;
    }
    save.mutate(
      {
        id: target.contest?.id ?? null,
        payload: {
          name: name.trim(),
          description: description.trim() || null,
          measure,
          startDate,
          endDate,
          insurerIds: newClients ? [] : insurerIds,
          productNames: newClients ? [] : productNames,
          participantAgentIds: agentIds,
          minimumThreshold: minimumValue,
          rewardType,
          topPlaces: rewardType === "TopPlaces" ? Number(topPlaces) : null,
          tiers: rewardType === "Tiers" ? parsedTiers : [],
          rewardDescription: rewardDescription.trim(),
        },
      },
      {
        onSuccess: (saved) => {
          toast.success(
            isNew
              ? `Zapisano szkic konkursu: ${saved.name}.`
              : `Zapisano zmiany: ${saved.name}.`,
          );
          handleClose();
        },
      },
    );
  }

  const products = (options.data?.productNames ?? []).map((p) => ({
    id: p,
    name: p,
  }));

  return (
    <Modal
      isOpen={target !== null}
      onClose={handleClose}
      title={isNew ? "Nowy konkurs" : "Edytuj konkurs"}
      className="max-w-2xl"
    >
      {options.isLoading ? (
        <div className="flex justify-center py-12">
          <Spinner />
        </div>
      ) : (
        <form onSubmit={handleSubmit} className="space-y-5">
          {save.error && (
            <Alert variant="error">
              {errorMessage(save.error, "Nie udało się zapisać konkursu")}
            </Alert>
          )}

          <Input
            label="Nazwa"
            value={name}
            onChange={(e) => {
              setName(e.target.value);
            }}
            placeholder="np. Jesienny maraton OC"
            required
            maxLength={150}
            autoComplete="off"
          />
          <Textarea
            label="Opis dla agentów (opcjonalnie)"
            value={description}
            onChange={(e) => {
              setDescription(e.target.value);
            }}
            rows={2}
            maxLength={2000}
          />

          <div className="grid gap-4 sm:grid-cols-3">
            <Select
              label="Miara"
              options={MEASURE_OPTIONS}
              value={measure}
              onChange={(v) => {
                setMeasure(v as ContestMeasure);
              }}
              className="sm:col-span-1"
            />
            <Input
              label="Od"
              type="date"
              value={startDate}
              onChange={(e) => {
                setStartDate(e.target.value);
              }}
              required
            />
            <Input
              label="Do"
              type="date"
              value={endDate}
              onChange={(e) => {
                setEndDate(e.target.value);
              }}
              required
            />
          </div>
          <p className="-mt-2 text-xs text-dim">{MEASURE_HINTS[measure]}</p>

          <div className="grid gap-5 sm:grid-cols-2">
            <PickList
              label="Towarzystwa"
              allLabel="Wszystkie towarzystwa"
              options={options.data?.insurers ?? []}
              selected={insurerIds}
              onChange={setInsurerIds}
              disabled={newClients}
            />
            <PickList
              label="Rodzaje produktów (z importów)"
              allLabel="Wszystkie produkty"
              options={products}
              selected={productNames}
              onChange={setProductNames}
              disabled={newClients}
            />
          </div>
          <PickList
            label="Uczestnicy"
            allLabel="Wszyscy agenci"
            options={options.data?.agents ?? []}
            selected={agentIds}
            onChange={setAgentIds}
          />

          <div className="space-y-4 border-t border-edge-soft pt-4">
            <div className="grid gap-4 sm:grid-cols-2">
              <Select
                label="Nagradzanie"
                options={REWARD_OPTIONS}
                value={rewardType}
                onChange={(v) => {
                  setRewardType(v as ContestRewardType);
                }}
              />
              <Input
                label={`Próg minimalny (${unit})${rewardType === "AboveThreshold" ? "" : " — opcjonalnie"}`}
                inputMode="decimal"
                value={minimum}
                onChange={(e) => {
                  setMinimum(e.target.value);
                }}
                placeholder="np. 10"
                hint="Poniżej progu agent nie dostaje nagrody, nawet z wysokim miejscem."
              />
            </div>

            {rewardType === "TopPlaces" && (
              <Input
                label="Ile miejsc nagradzamy"
                type="number"
                min={1}
                max={50}
                value={topPlaces}
                onChange={(e) => {
                  setTopPlaces(e.target.value);
                }}
                className="sm:max-w-40"
              />
            )}

            {rewardType === "Tiers" && (
              <div className="space-y-2">
                <p className="text-sm font-medium text-sub">
                  Progi ({unit}) i nagrody
                </p>
                {tiers.map((tier, i) => (
                  <div
                    // biome-ignore lint/suspicious/noArrayIndexKey: rows have no identity of their own
                    key={i}
                    className="flex items-start gap-2"
                  >
                    <Input
                      aria-label={`Próg ${i + 1}`}
                      inputMode="decimal"
                      value={tier.threshold}
                      onChange={(e) => {
                        setTiers(
                          tiers.map((t, j) =>
                            j === i ? { ...t, threshold: e.target.value } : t,
                          ),
                        );
                      }}
                      placeholder={String((i + 1) * 20)}
                      className="w-28"
                    />
                    <Input
                      aria-label={`Nagroda za próg ${i + 1}`}
                      value={tier.reward}
                      onChange={(e) => {
                        setTiers(
                          tiers.map((t, j) =>
                            j === i ? { ...t, reward: e.target.value } : t,
                          ),
                        );
                      }}
                      placeholder="np. Voucher 200 zł"
                      maxLength={500}
                      className="flex-1"
                    />
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      aria-label={`Usuń próg ${i + 1}`}
                      disabled={tiers.length === 1}
                      onClick={() => {
                        setTiers(tiers.filter((_, j) => j !== i));
                      }}
                    >
                      <Trash2 size={16} aria-hidden />
                    </Button>
                  </div>
                ))}
                {tiers.length < 10 && (
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    leftIcon={<Plus size={14} aria-hidden />}
                    onClick={() => {
                      setTiers([...tiers, { threshold: "", reward: "" }]);
                    }}
                  >
                    Dodaj próg
                  </Button>
                )}
              </div>
            )}

            <Textarea
              label="Nagrody — opis"
              value={rewardDescription}
              onChange={(e) => {
                setRewardDescription(e.target.value);
              }}
              placeholder="np. 1. miejsce — weekend w SPA, 2. — voucher 500 zł, 3. — voucher 200 zł"
              rows={3}
              maxLength={2000}
              required
            />
          </div>

          {problem && name.trim() && (
            <p className="text-xs text-dim">{problem}</p>
          )}

          <div className="flex justify-end gap-3 border-t border-edge-soft pt-4">
            <Button variant="ghost" type="button" onClick={handleClose}>
              Anuluj
            </Button>
            <Button
              type="submit"
              isLoading={save.isPending}
              disabled={problem !== null}
            >
              {isNew ? "Zapisz szkic" : "Zapisz"}
            </Button>
          </div>
        </form>
      )}
    </Modal>
  );
}
